import React from 'react';
import { List, Show, useShowContext, useDataProvider, Identifier } from 'react-admin';
import { useParams } from 'react-router-dom';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import { MessageSquare, Trash2, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Avatar } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider } from '../types/dataProvider';

/* ============================================================
   Direct chats — Latio design system (derived). List + paginated transcript
   detail with include-deleted toggle and reason-based soft deletion.
   ============================================================ */

interface DirectChatParticipant {
  id: number;
  name: string;
  username?: string;
  email: string;
  profile_picture_url?: string;
  is_trainer: boolean;
}
interface DirectChatListItem {
  id: number;
  chat_id: number;
  user1: DirectChatParticipant;
  user2: DirectChatParticipant;
  total_messages: number;
  messages_today: number;
  messages_this_week: number;
  has_recent_activity: boolean;
  created_at: string;
  last_message_at?: string;
  last_message_content?: string;
  last_message_sender_name?: string;
}
interface DirectChatMessage {
  id: number;
  chat_id: number;
  message: string;
  message_type: string;
  created_at: string;
  sender_name?: string;
  sender_username?: string;
  sender_profile_picture_url?: string;
  deleted_at?: string;
  delete_reason?: string;
  event_type?: string;
}

const fmtDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

function ParticipantCell({ p }: { p: DirectChatParticipant }) {
  return (
    <div className="flex items-center gap-2.5">
      <Avatar src={p.profile_picture_url} name={p.name} size={28} />
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-semibold text-ink">{p.name}</span>
          {p.is_trainer && <span className="rounded-pill bg-indigo-tint px-1.5 py-0.5 text-[10px] font-semibold text-indigo">Coach</span>}
        </div>
        <div className="truncate text-[12px] text-ink-3">{p.email}</div>
      </div>
    </div>
  );
}

/* ---- List ---- */
const DirectChatsListBody = () => {
  const columns: DataColumn<DirectChatListItem>[] = [
    { key: 'chat_id', header: 'ID', width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.chat_id}</span> },
    { key: 'user1', header: 'User 1', cell: (r) => (r.user1 ? <ParticipantCell p={r.user1} /> : <span className="text-ink-3">—</span>) },
    { key: 'user2', header: 'User 2', cell: (r) => (r.user2 ? <ParticipantCell p={r.user2} /> : <span className="text-ink-3">—</span>) },
    {
      key: 'messages',
      header: 'Messages',
      cell: (r) => (
        <div>
          <div className="font-medium text-ink">{r.total_messages || 0}</div>
          <div className="text-[12px] text-ink-3">Today {r.messages_today || 0}</div>
        </div>
      ),
    },
    {
      key: 'last',
      header: 'Last message',
      cell: (r) => (
        <div className="max-w-[260px]">
          <div className="truncate text-ink">{r.last_message_content || <span className="text-ink-3">No messages</span>}</div>
          {r.last_message_sender_name && <div className="truncate text-[12px] text-ink-3">by {r.last_message_sender_name}</div>}
        </div>
      ),
    },
    { key: 'last_message_at', header: 'Activity', sortable: true, cell: (r) => fmtDateTime(r.last_message_at) },
  ];
  return (
    <ListScreen tokens={null} count={null}>
      <DataTable columns={columns} rowClick="show" emptyMessage="No direct chats found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

export const DirectChatList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'last_message_at', order: 'DESC' }} perPage={50} component="div">
    <SearchIntegration filterKey="q" />
    <DirectChatsListBody />
  </List>
);

/* ---- Message bubble ---- */
function DirectMessageRow({ message, onDelete }: { message: DirectChatMessage; onDelete: (id: number) => void }) {
  const isDeleted = !!message.deleted_at;
  const isSystem = message.message_type === 'system' || !!message.event_type;

  if (isSystem) {
    return (
      <div className="my-3 flex flex-col items-center gap-1">
        <span className="rounded-pill bg-surface-2 px-3 py-1 text-[13px] text-ink-2">{message.message}</span>
        <span className="text-[12px] text-ink-3">{new Date(message.created_at).toLocaleString()}</span>
      </div>
    );
  }

  return (
    <div className="group flex gap-3 py-2">
      <Avatar src={message.sender_profile_picture_url} name={message.sender_name} size={32} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-ink">{message.sender_name || 'Unknown'}</span>
          {message.sender_username && <span className="text-[12px] text-ink-3">@{message.sender_username}</span>}
          <span className="text-[12px] text-ink-3">{new Date(message.created_at).toLocaleString()}</span>
          {isDeleted && <Badge className="bg-error-bg text-[11px] text-error">Deleted</Badge>}
        </div>
        {isDeleted ? (
          <div className="mt-0.5">
            <p className="text-sm italic text-ink-3 line-through">{message.message}</p>
            {message.delete_reason && <p className="mt-0.5 text-[12px] text-error">Reason: {message.delete_reason}</p>}
          </div>
        ) : (
          <p className="mt-0.5 text-sm text-ink">{message.message}</p>
        )}
      </div>
      {!isDeleted && (
        <button
          type="button"
          onClick={() => onDelete(message.id)}
          title="Delete message"
          className="invisible inline-flex size-7 shrink-0 items-center justify-center rounded-pill text-ink-2 hover:bg-error-bg hover:text-error group-hover:visible"
        >
          <Trash2 className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/* ---- Detail ---- */
function DetailCard({ title, icon: Icon, action, children, className }: { title?: string; icon?: any; action?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      {(title || action) && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-[15px] font-bold text-ink">
            {Icon && <Icon className="size-4 text-ink-2" />}
            {title}
          </h3>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

const DirectChatShowContent = () => {
  const { record, isLoading } = useShowContext<DirectChatListItem>();
  const { id } = useParams();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const { setData } = useBreadcrumbContext();

  const [messages, setMessages] = React.useState<DirectChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = React.useState(true);
  const [includeDeleted, setIncludeDeleted] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [deleteId, setDeleteId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (record) setData({ resourceId: String(record.chat_id), resourceName: `Chat #${record.chat_id}`, resourceType: 'direct-chat' });
  }, [record, setData]);

  const fetchMessages = React.useCallback(async () => {
    if (!id) return;
    setMessagesLoading(true);
    try {
      const { data } = await dataProvider.getDirectChatMessages({ chatId: id, page, limit: 50, includeDeleted });
      setMessages(data?.messages || []);
      setTotalPages(data?.total_pages || 1);
    } catch {
      toastError('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  }, [id, page, includeDeleted, dataProvider]);

  React.useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  React.useEffect(() => {
    setPage(1);
  }, [includeDeleted]);

  const handleDelete = async (reason?: string) => {
    if (deleteId === null || !id) return;
    try {
      await dataProvider.softDeleteDirectChatMessage({ chatId: id as Identifier, messageId: deleteId, reason: reason || '' });
      toastSuccess('Message deleted');
      fetchMessages();
    } catch {
      toastError('Failed to delete message');
    }
  };

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading chat…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Chat not found</div>;

  const facts = [
    { label: 'Total', value: record.total_messages || 0 },
    { label: 'Today', value: record.messages_today || 0 },
    { label: 'This week', value: record.messages_this_week || 0 },
    { label: 'Status', value: record.has_recent_activity ? 'Active' : 'Inactive' },
  ];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
          Direct chat #{record.chat_id}
        </h1>
        <p className="mt-1 text-sm text-ink-2">Admin view — full message history</p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {facts.map((f) => (
          <DetailCard key={f.label}>
            <div className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-3">{f.label}</div>
            <div className="mt-1 text-[22px] font-bold text-ink">{f.value}</div>
          </DetailCard>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {([record.user1, record.user2] as DirectChatParticipant[]).map((u, i) =>
          u ? (
            <DetailCard key={i} title={`Participant ${i + 1}`}>
              <div className="flex items-center gap-3">
                <Avatar src={u.profile_picture_url} name={u.name} size={40} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-ink">{u.name}</span>
                    {u.is_trainer && <span className="rounded-pill bg-indigo-tint px-1.5 py-0.5 text-[10px] font-semibold text-indigo">Coach</span>}
                  </div>
                  {u.username && <div className="text-[13px] text-ink-2">@{u.username}</div>}
                  <div className="text-[13px] text-ink-2">{u.email}</div>
                </div>
              </div>
            </DetailCard>
          ) : null,
        )}
      </div>

      <DetailCard
        title="Messages"
        icon={MessageSquare}
        action={
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-[13px] text-ink-2">
              <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} className="rounded" />
              Show deleted
            </label>
            <button
              type="button"
              onClick={fetchMessages}
              disabled={messagesLoading}
              className="inline-flex h-8 items-center gap-1.5 rounded-pill border border-hairline px-3 text-[13px] font-semibold text-ink-2 hover:text-ink disabled:opacity-50"
            >
              <RefreshCw className="size-3.5" />
              Refresh
            </button>
          </div>
        }
      >
        {messagesLoading ? (
          <p className="py-8 text-center text-sm text-ink-2">Loading messages…</p>
        ) : messages.length === 0 ? (
          <div className="py-10 text-center text-ink-3">
            <MessageSquare className="mx-auto mb-2 size-10 text-hairline" />
            <p className="text-sm">No messages found</p>
          </div>
        ) : (
          <ScrollArea className="h-[480px] pr-2">
            <div className="divide-y divide-hairline">
              {messages.map((m) => (
                <DirectMessageRow key={m.id} message={m} onDelete={(mid) => setDeleteId(mid)} />
              ))}
            </div>
          </ScrollArea>
        )}

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-hairline pt-4">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || messagesLoading}
              className="inline-flex h-8 items-center gap-1 rounded-pill border border-hairline px-3 text-[13px] font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
            >
              <ChevronLeft className="size-4" />
              Previous
            </button>
            <span className="text-[13px] text-ink-2">Page {page} of {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || messagesLoading}
              className="inline-flex h-8 items-center gap-1 rounded-pill border border-hairline px-3 text-[13px] font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
            >
              Next
              <ChevronRight className="size-4" />
            </button>
          </div>
        )}
      </DetailCard>

      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={(o) => !o && setDeleteId(null)}
        tone="destructive"
        title="Delete this message?"
        consequence="Soft-deletes the message; it stays visible to admins with “Show deleted” enabled."
        confirmLabel="Delete message"
        reasonRequired
        reasonChips={['Inappropriate content', 'Spam', 'Harassment']}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export const DirectChatShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <DirectChatShowContent />
  </Show>
);
